import Color from '@/Enums/Color.enum';
import FractionDigits from '@/Enums/Localization/FractionDigits.enum';
import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import DollarCircleIcon from '@/components/Icons/DollarCircleIcon';
import MegaphoneIcon from '@/components/Icons/MegaphoneIcon';
import { numberToPercent, numberToUsFormat, numberToUsd } from '@/helpers/valueFormatter/numberFormatter.helpers';
import type { ElementType, ReactNode } from 'react';
import AIAudioIcon from '../Icons/AIAudioIcon';
import AddToListIcon from '../Icons/AddToListIcon';
import AdsClickIcon from '../Icons/AdsClickIcon';
import AnalyticsIcon from '../Icons/AnalyticsIcon';
import ExportPlaylistIcon from '../Icons/ExportPlaylistIcon';
import MoneyExchangeIcon from '../Icons/MoneyExchangeIcon';
import PercentIcon from '../Icons/PercentIcon';
import SearchDollarIcon from '../Icons/SearchDollarIcon';
import TargetIcon from '../Icons/TargetIcon';

interface IMetricOption {
  metric: TotalMetric;
  label: ReactNode;
  valueConverter?: (...args: any[]) => ReactNode;
  Icon: ElementType;
  color: Color;
}

export const METRIC_OPTIONS: Record<TotalMetric, IMetricOption> = {
  [TotalMetric.VCR]: {
    metric: TotalMetric.VCR,
    label: 'Video Completion Rate (VCR)',
    valueConverter: numberToPercent,
    Icon: DollarCircleIcon,
    color: Color.MetricPurple,
  },
  [TotalMetric.CTR]: {
    metric: TotalMetric.CTR,
    label: 'Click-Through Rate (CTR)',
    valueConverter: (value: number) => numberToPercent(value, { maximumFractionDigits: FractionDigits.Ctr }),
    Icon: DollarCircleIcon,
    color: Color.MetricPurple,
  },
  [TotalMetric.LTR]: {
    metric: TotalMetric.LTR,
    label: 'Listen-Through Rate (LTR)',
    valueConverter: (value: number) => numberToPercent(value, { maximumFractionDigits: FractionDigits.Ltr }),
    Icon: DollarCircleIcon,
    color: Color.MetricPurple,
  },
  [TotalMetric.Impressions]: {
    metric: TotalMetric.Impressions,
    label: 'Impressions',
    Icon: MegaphoneIcon,
    color: Color.MetricBlue,
  },
  [TotalMetric.Reach]: {
    metric: TotalMetric.Reach,
    label: 'Reach',
    Icon: TargetIcon,
    color: Color.MetricDarkPurple,
  },
  [TotalMetric.Frequency]: {
    metric: TotalMetric.Frequency,
    label: 'Frequency',
    Icon: AdsClickIcon,
    color: Color.MetricGreen,
  },
  [TotalMetric.Spend]: {
    metric: TotalMetric.Spend,
    label: 'Spend',
    valueConverter: numberToUsd,
    Icon: MegaphoneIcon,
    color: Color.MetricPurple,
  },
  //#region foot traffic
  [TotalMetric.FootTrafficVisits]: {
    metric: TotalMetric.FootTrafficVisits,
    label: 'Visits',
    Icon: TargetIcon,
    color: Color.MetricDarkPurple,
  },
  [TotalMetric.FootTrafficLiftPercent]: {
    metric: TotalMetric.FootTrafficLiftPercent,
    label: TotalMetric.FootTrafficLiftPercent,
    Icon: PercentIcon,
    color: Color.MetricPurple,
  },
  [TotalMetric.VisitsConversionRate]: {
    metric: TotalMetric.VisitsConversionRate,
    label: TotalMetric.VisitsConversionRate,
    valueConverter: numberToPercent,
    Icon: PercentIcon,
    color: Color.MetricPurple,
  },
  [TotalMetric.YoyPercentChange]: {
    metric: TotalMetric.YoyPercentChange,
    label: TotalMetric.YoyPercentChange,
    valueConverter: numberToPercent,
    Icon: AnalyticsIcon,
    color: Color.MetricGreen,
  },
  [TotalMetric.TwoYearBaselineChange]: {
    metric: TotalMetric.TwoYearBaselineChange,
    label: TotalMetric.TwoYearBaselineChange,
    valueConverter: numberToPercent,
    Icon: AnalyticsIcon,
    color: Color.MetricDarkPurple,
  },
  [TotalMetric.AvgWeeklyUniqueVisitors]: {
    metric: TotalMetric.AvgWeeklyUniqueVisitors,
    label: TotalMetric.AvgWeeklyUniqueVisitors,
    Icon: TargetIcon,
    color: Color.MetricDeepBlue,
  },
  //#endregion
  //#region offfline sales
  [TotalMetric.TotalSales]: {
    metric: TotalMetric.TotalSales,
    label: 'Total Records Uploaded',
    Icon: AnalyticsIcon,
    color: Color.MetricGreen,
  },
  [TotalMetric.MatchedSales]: {
    metric: TotalMetric.MatchedSales,
    label: 'Matched Records',
    Icon: SearchDollarIcon,
    color: Color.MetricDeepBlue,
  },
  [TotalMetric.MatchedConversions]: {
    metric: TotalMetric.MatchedConversions,
    label: 'Matched Conversions',
    Icon: AddToListIcon,
    color: Color.MetricLightBlue,
  },
  [TotalMetric.ExtrapolatedConversions]: {
    metric: TotalMetric.ExtrapolatedConversions,
    label: 'Extrapolated Conversions',
    valueConverter: (value: number) => numberToUsFormat(value, { maximumFractionDigits: 0 }),
    Icon: MoneyExchangeIcon,
    color: Color.MetricDarkPurple2,
  },
  [TotalMetric.MediaSalesPercent]: {
    metric: TotalMetric.MediaSalesPercent,
    label: '% of Total Conversions From Media',
    valueConverter: numberToPercent,
    Icon: ExportPlaylistIcon,
    color: Color.MetricGreen2,
  },
  [TotalMetric.UniqueCVR]: {
    metric: TotalMetric.UniqueCVR,
    label: 'Unique CVR',
    valueConverter: (value: number) =>
      numberToPercent(value, {
        minimumFractionDigits: FractionDigits.Extended,
        maximumFractionDigits: FractionDigits.Extended,
      }),
    Icon: AIAudioIcon,
    color: Color.MetricPink,
  },
  //#endregion
  //#region esm
  [TotalMetric.CostPerClick]: {
    metric: TotalMetric.CostPerClick,
    label: 'Cost Per Click (CPC)',
    Icon: DollarCircleIcon,
    color: Color.MetricGreen,
    valueConverter: numberToUsd,
  },
  [TotalMetric.CostPerImpression]: {
    metric: TotalMetric.CostPerImpression,
    label: 'Cost Per Impression (CPM)',
    Icon: DollarCircleIcon,
    color: Color.MetricGreen,
    valueConverter: numberToUsd,
  },
  [TotalMetric.CostPerConversion]: {
    metric: TotalMetric.CostPerConversion,
    label: 'Cost Per Conversion',
    Icon: DollarCircleIcon,
    color: Color.MetricPurple,
    valueConverter: numberToUsd,
  },
  [TotalMetric.EngagementRate]: {
    metric: TotalMetric.EngagementRate,
    label: 'Engagement Rate',
    Icon: DollarCircleIcon,
    color: Color.MetricPurple,
    valueConverter: numberToPercent,
  },
  //#endregion
};
